import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'

import { useExcludedPforStorefrontIds } from '@/buyer-portal/components/features/listing/hooks/PforExclusionContext'
import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import { FeatureFlags } from '@/helpers/featureFlags'

/**
 * Whether PFOR must be hidden for the listing's storefront: either listed in the
 * LaunchDarkly `excludedStorefrontIds` / `excludedStorefrontSlugs` arrays, or in
 * the DB-backed list from `PforExclusionProvider` (when
 * `enable-pfor-storefront-exclusion` is on).
 */
export const useIsPforStorefrontExcluded = (
  listing: OrchestratedListingDetails | undefined
) => {
  const config = useFlag(FeatureFlags.ENABLE_LDP_PROBLEM_FREE_ORDER_RATE)
  const pforExclusionEnabled = useFlag(
    FeatureFlags.ENABLE_PFOR_STOREFRONT_EXCLUSION
  )
  const excludedPforStorefrontIds = useExcludedPforStorefrontIds()

  const storefrontId = listing?.seller.storefront?._id
  return storefrontId
    ? (config.excludedStorefrontIds ?? []).includes(storefrontId) ||
        (config.excludedStorefrontSlugs ?? []).includes(
          listing.seller.storefront?.slug ?? ''
        ) ||
        (pforExclusionEnabled &&
          excludedPforStorefrontIds.includes(storefrontId))
    : false
}

/**
 * Returns the two `facility_performance_rate` fields required by analytics events
 * on the Listing Detail Page. Mirrors the visibility logic of the PFOR cards
 * (`ProblemFreeOrderRate`, `FacilityStatsCard`). For an excluded storefront the rate is `null` so events
 * never carry a score the buyer was not shown.
 *
 * @param listing - The listing details object.
 */
export const useProblemFreeOrderRateAnalytics = (
  listing: OrchestratedListingDetails | undefined
) => {
  const config = useFlag(FeatureFlags.ENABLE_LDP_PROBLEM_FREE_ORDER_RATE)
  const isLoggedIn = useIsLoggedIn()
  const isStorefrontExcluded = useIsPforStorefrontExcluded(listing)

  const facilityPerformanceRate = isStorefrontExcluded
    ? null
    : ((listing as { problemFreeOrderRate?: number | null } | undefined)
        ?.problemFreeOrderRate ?? null)

  const isFacilityPerformanceRateShown =
    config.enabled && isLoggedIn && !isStorefrontExcluded

  return {
    facility_performance_rate: facilityPerformanceRate,
    is_facility_performance_rate_shown: isFacilityPerformanceRateShown,
  }
}
